import React, { createContext, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import API from '../../../services/API'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {
  AlertTitle,
  AppBar,
  Box,
  Chip,
  Tab,
  Tabs,
  useTheme,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import Requirements from './Requirements'
import moment from 'moment'
import { UserContext } from '../../../App'
import { canEditLoadLists } from '../makeReadyModule/makeReadyAccess'

export const MakeReadyContext = createContext({})

const MakeReady = () => {
  const { callsignAssetID } = useParams()
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [noMatchingLoadList, setNoMatchingLoadList] = useState(true)
  const [loadList, setLoadList] = useState({})
  const [inventory, setInventory] = useState({ items: [] })
  const [assetVehicle, setAssetVehicle] = useState({})
  const navigate = useNavigate()
  const theme = useTheme()
  const { user } = useContext(UserContext)
  const canEditLoadList = canEditLoadLists(user)

  useEffect(() => {
    API.post('/getmakereadydata', {
      callsignAssetID,
    })
      .then((res) => {
        if (res.status === 200) {
          setLoadList(res.data.loadList)
          setInventory(res.data.inventory)
          setAssetVehicle(res.data.assetVehicle)
        } else {
          setFetchError(true)
          if (res.data?.error === 'No registered Load List') {
            setNoMatchingLoadList(true)
          }
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  return (
    <MakeReadyContext.Provider
      value={{
        loadList,
        inventory,
        setInventory,
        callsignAssetID,
        assetVehicle,
      }}
    >
      <Stack gap={1}>
        <Stack
          gap={1}
          direction={{ xs: 'column', lg: 'row' }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack gap={1}>
            <Typography variant="title2">Make Ready</Typography>
            <Stack gap={1} direction="row" alignItems="center">
              <Typography variant="title3">{loadList.title}</Typography>
              <Chip
                color="anMainBlue"
                label={loadList.type}
                size="small"
                sx={{ borderRadius: 1, display: !loadList.title && 'none' }}
              />
              <Chip
                color="anPurple"
                label={assetVehicle?.callSign ?? assetVehicle?.assetID}
                size="small"
                sx={{ borderRadius: 1, display: !loadList.title && 'none' }}
              />
              {canEditLoadList && loadList?._id && (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() =>
                    navigate(`/make-ready-load-lists/${loadList._id}`)
                  }
                >
                  Edit Load List
                </Button>
              )}
            </Stack>
          </Stack>
          {inventory?.earliestExpiryDate && <Stack direction="row" gap={1}>
            <Typography variant="title2" fontSize="1rem" fontWeight="strong">
              <b>Earliest Expiry:</b>
            </Typography>
            <Typography variant="title2" fontSize="1rem">
                {moment(inventory.earliestExpiryDate).format('DD/MM/YYYY')}
            </Typography>
          </Stack>}
        </Stack>
        {loading && <LinearProgress />}
        {fetchError && (
          <Stack gap={1}>
            <Alert severity="error">
              <AlertTitle>
                {noMatchingLoadList
                  ? 'No valid load list has been selected for this asset.'
                  : 'Unable to fetch Make Ready information for this asset. Please try again.'}
              </AlertTitle>
              <Button
                onClick={() => navigate('/make-ready-overview')}
                variant="outlined"
                color="error"
              >
                Return to Search
              </Button>
            </Alert>
          </Stack>
        )}
        <Requirements />
      </Stack>
    </MakeReadyContext.Provider>
  )
}

export default MakeReady
